import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import {
  DismissSuggestionResponse,
  TradeSuggestionsCountResponse,
  TradeSuggestionsResponse,
  ValidateSuggestionResponse,
} from 'types/interfaces/tradeSuggestion';

// Matches the shipped surface backend: GET list (ACTIVE only, ranked, lazy
// expiry, emptyReason from totalCount), POST validate (mutates on failure —
// flips the suggestion to INVALIDATED, releasing its rows), POST dismiss
// (200-idempotent). Consumers must treat any error as "no suggestions" and
// degrade — never a blank strip or a stuck spinner.
export const tradeSuggestionsApi = createApi({
  reducerPath: 'tradeSuggestionsApi',
  baseQuery,
  tagTypes: ['Suggestions'],
  endpoints: (builder) => ({
    getTradeSuggestions: builder.query<
      TradeSuggestionsResponse,
      { page?: number; limit?: number } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.page) params.set('page', String(args.page));
        if (args?.limit) params.set('limit', String(args.limit));
        const qs = params.toString();
        return {
          url: `/trade-suggestions${qs ? `?${qs}` : ''}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['Suggestions'],
    }),

    // Live count for the Suggested tab's inline count and the suggestions half
    // of the nav's single Offers badge. Deliberately shares
    // the 'Suggestions' tag with the list: dismiss invalidates that tag, so a
    // pass refetches BOTH the surface and the badge from the same server-side
    // predicate and they cannot drift apart. This tag is the whole post-pass
    // refetch wiring — dismiss answers { success } and carries no count, so the
    // badge has no other source.
    getTradeSuggestionsCount: builder.query<TradeSuggestionsCountResponse, void>({
      query: () => ({
        url: '/trade-suggestions/count',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Suggestions'],
    }),

    // Fired at tap time, immediately before seeding the builder. POST because a
    // failed validate transitions the suggestion to INVALIDATED server-side; a
    // failure must also refetch the list so the released row disappears.
    validateSuggestion: builder.mutation<ValidateSuggestionResponse, string>({
      query: (suggestionId) => ({
        url: `/trade-suggestions/${suggestionId}/validate`,
        method: 'POST',
        requiresAuth: true,
      }),
      // NOTE: no automatic tag invalidation. A failed validate INVALIDATES the
      // suggestion server-side, but the card must keep rendering its stale
      // message (naming the card) until the user acknowledges it — an immediate
      // refetch would unmount the card mid-read. Surfaces refetch on
      // acknowledgement via tradeSuggestionsApi.util.invalidateTags.
    }),

    dismissSuggestion: builder.mutation<DismissSuggestionResponse, string>({
      query: (suggestionId) => ({
        url: `/trade-suggestions/${suggestionId}/dismiss`,
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: ['Suggestions'],
    }),
  }),
});

export const {
  useGetTradeSuggestionsQuery,
  useGetTradeSuggestionsCountQuery,
  useValidateSuggestionMutation,
  useDismissSuggestionMutation,
} = tradeSuggestionsApi;
